import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Input, Select, Textarea, Btn, RNLLoader, Modal, Spinner } from '../UI';
import { fmt } from '../../utils';
import * as api from '../../api';
import { PAYMENT_TYPE_OPTIONS, parsePvErrorMessage } from './pvUtils';
import VoucherReviewDetail from './VoucherReviewDetail';
import VoucherEditModal from './VoucherEditModal';
import VoucherApprovalHistory from './VoucherApprovalHistory';
import VoucherPrintView from './VoucherPrintView';
import VoucherOutcomeModal from './VoucherOutcomeModal';
import Pagination from '../requisition/Pagination';

const NAVY = '#0b318b';

// Mirrors REVIEW_STATUS_ORDER in backend/app/schemas/voucher_review_schemas.py.
// "Under Review", "Partially Processed" and "Fully Processed" are absent on
// purpose: the Review action is not implemented and the two processing states
// depend on Process Payment, so they would be tabs that never fill.
const TABS = [
  'All', 'Awaiting Review', 'Approved for Payment', 'On Hold',
  'Returned', 'Rejected', 'Released to Expense Register',
];

const STATUS_COLORS = {
  'Awaiting Review': { bg: '#fef3c7', color: '#b45309' },
  'Approved for Payment': { bg: '#dcfce7', color: '#15803d' },
  'On Hold': { bg: '#ffedd5', color: '#c2410c', border: '#f97316' },
  'Returned': { bg: '#fef3c7', color: '#a16207', border: '#eab308' },
  'Rejected': { bg: '#fee2e2', color: '#b91c1c', border: '#ef4444' },
  'Released to Expense Register': { bg: '#dbeafe', color: '#1d4ed8' },
};

const TILES = [
  ['total_posted', 'Total Posted', '#0f172a'],
  ['awaiting_review', 'Awaiting Review', '#b45309'],
  ['approved', 'Approved', '#15803d'],
  ['on_hold', 'On Hold', '#c2410c'],
  ['returned', 'Returned', '#a16207'],
  ['rejected', 'Rejected', '#b91c1c'],
  ['ready_for_release', 'Ready for Release', '#1d4ed8'],
  ['released_today', 'Released Today', '#0f766e'],
  ['total_net_payable', 'Total Net Payable', '#15803d', true],
  ['total_amount_paid', 'Total Amount Paid', '#1d4ed8', true],
  ['outstanding', 'Outstanding', '#c2410c', true],
];

// title, key, whether it needs a comment, and which rows it applies to.
const ACTIONS = [
  { key: 'view', title: 'View' },
  { key: 'edit', title: 'Edit' },
  { key: 'approve', title: 'Approve', workflow: true },
  { key: 'hold', title: 'Hold' },
  { key: 'return', title: 'Return', workflow: true, needsComment: true },
  { key: 'reject', title: 'Reject', workflow: true, needsComment: true },
  { key: 'history', title: 'Approval History' },
  { key: 'print', title: 'Print' },
  { key: 'export', title: 'Export' },
  { key: 'release', title: 'Release to Expense Register' },
];

// Inline SVG rather than text glyphs: the old set relied on characters like
// ⭳ and 🖨 whose rendering varies by font and platform — Export in particular
// fell back to a box on most systems. SVG also lets each icon take the colour
// of the action it performs.
const ico = { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

const ICONS = {
  // Eye and clock stay as glyphs — they render cleanly everywhere and read
  // better at this size. The rest are SVG because their glyphs (⭳ above all)
  // had poor font coverage and fell back to boxes.
  view: '👁',
  edit: (<svg {...ico}><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>),
  approve: (<svg {...ico}><polyline points="20 6 9 17 4 12" /></svg>),
  hold: (<svg {...ico}><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>),
  // Shown in place of the pause bars when the voucher is ALREADY held, so the
  // button says what pressing it will do — resume — rather than what the
  // current state is.
  resume: (<svg {...ico}><polygon points="6 3 20 12 6 21 6 3" /></svg>),
  return: (<svg {...ico}><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></svg>),
  reject: (<svg {...ico}><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>),
  history: '🕘',
  print: (<svg {...ico}><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>),
  export: (<svg {...ico}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>),
  release: (<svg {...ico}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>),
};

// Each action carries the colour of what it does, so the row can be read at a
// glance without hovering for tooltips.
const ACTION_COLOR = {
  view: NAVY,
  edit: '#eab308',      // pencil — matches the amber edit used in the inbox tables
  approve: '#16a34a',
  hold: '#f59e0b',
  resume: '#16a34a',
  return: '#f97316',
  reject: '#ef4444',
  history: NAVY,
  print: NAVY,
  export: NAVY,
  release: '#16a34a',
};

// The API returns the raw enum value (supplier_payment, mobilisation_advance).
// Show the human label instead, falling back to a de-underscored version so an
// enum added on the backend before the frontend still reads sensibly.
const paymentTypeLabel = (value) => {
  if (!value) return '—';
  const opt = PAYMENT_TYPE_OPTIONS.find(o => o.value === value);
  return opt ? opt.label : value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const iconBtn = (color, disabled) => ({
  // 10 buttons x 23px + 9 x 2px gaps + padding fits the 280px ACTIONS column;
  // at 26px they needed 290px and the last one clipped.
  width: 23, height: 23, borderRadius: 6, border: `1px solid ${disabled ? '#e2e8f0' : color}`,
  background: disabled ? '#f8fafc' : '#fff', color: disabled ? '#cbd5e1' : color,
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, lineHeight: 1,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
});

// Gridlines live on the cells, not the table, and the table uses
// border-collapse: separate. With `collapse`, borders belong to the table
// rather than the cell — and browsers do not paint them on position: sticky
// cells, so every frozen column would lose its gridlines. Each cell draws only
// its right and bottom edge so adjacent borders cannot double up.
const GRID = '1px solid #cbd5e1';
const TD = {
  padding: '7px 9px', fontSize: 11.5, whiteSpace: 'nowrap', textAlign: 'center',
  borderRight: GRID, borderBottom: GRID,
};
const TH = {
  padding: '8px 9px', fontSize: 10, fontWeight: 800, letterSpacing: 0.3,
  textTransform: 'uppercase', whiteSpace: 'nowrap', textAlign: 'center',
  borderRight: '1px solid rgba(255,255,255,0.28)', borderBottom: '1px solid #243b64',
};

// Columns frozen against horizontal scroll, in order: caret, Actions, Voucher
// Ref, Requisition Ref, Voucher Date, Vendor. Sticky positioning needs a fixed
// width AND a known left offset per column, so the widths are declared here and
// the offsets derived — keeping them in one place is what stops the header and
// body drifting apart by a pixel as columns change.
// Actions is sized from its contents, not guessed: 10 buttons x 24px + 9 gaps
// x 2px + 12px cell padding = 270px, rounded to 280 for margin. Undersize it
// and the next frozen column — painted on top — silently clips the last
// buttons, which is how Release went missing.
const FROZEN_WIDTHS = [30, 280, 122, 132, 96, 160];
const FROZEN_LEFT = FROZEN_WIDTHS.reduce(
  (acc, w, i) => [...acc, (acc[i] ?? 0) + w], [0],
);
const FROZEN_COUNT = FROZEN_WIDTHS.length;

/** Sticky styling for a frozen cell. `bg` must be opaque — scrolling columns
 *  pass underneath, and a transparent frozen cell would show them through. */
const frozen = (i, bg, isHeader) => (i >= FROZEN_COUNT ? {} : {
  position: 'sticky',
  left: FROZEN_LEFT[i],
  width: FROZEN_WIDTHS[i],
  minWidth: FROZEN_WIDTHS[i],
  maxWidth: FROZEN_WIDTHS[i],
  background: bg,
  zIndex: isHeader ? 3 : 1,
  // A hairline on the last frozen column marks where the scroll begins.
  ...(i === FROZEN_COUNT - 1 ? { boxShadow: '2px 0 4px -2px rgba(0,0,0,0.25)' } : {}),
});

/**
 * Voucher Review & Release — the final workflow step.
 *
 * Replaces the old MD/DMD Approval Queue. Who may act here is not hardcoded:
 * it comes from the roles configured on the final PAYMENT_VOUCHER workflow
 * step, fetched alongside the queue, so reassigning that step in Admin →
 * Workflow Configs reassigns this screen.
 */
export default function VoucherReviewRelease({ toast, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [allowedRoles, setAllowedRoles] = useState([]);
  const [headerOpen, setHeaderOpen] = useState(true);
  const [tab, setTab] = useState('All');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [advanceOnly, setAdvanceOnly] = useState('');

  const [rvPage, setRvPage] = useState({ skip: 0, limit: 10 });

  // Client-side filters — just reset to the first page when they change so
  // the visible rows are always from the start of the filtered set.
  useEffect(() => {
    setRvPage(p => p.skip === 0 ? p : { ...p, skip: 0 });
  }, [tab, search, dateFrom, dateTo, typeFilter, deptFilter, advanceOnly]);

  const [expanded, setExpanded] = useState({});
  const [detail, setDetail] = useState({});
  const [pending, setPending] = useState(null);   // { action, row }
  const [comment, setComment] = useState('');
  const [acting, setActing] = useState(false);
  // Synchronous double-submit guard — see runAction.
  const actingRef = useRef(false);
  const [actionError, setActionError] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [printing, setPrinting] = useState(null);   // { voucher, history }
  const [editing, setEditing] = useState(null);    // full voucher being corrected
  // The expanded panel is pinned to the visible width of the scroll container.
  // Without this it inherits the table's 1900px and scrolls sideways with the
  // columns, sliding underneath the frozen ones.
  const scrollRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  const load = async ({ silent = false, skip = rvPage.skip, limit = rvPage.limit } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [queue, roles] = await Promise.all([
        api.getVoucherReviewQueue(skip, limit),
        api.getVoucherReviewAllowedRoles().catch(() => ({ allowed_roles: [] })),
      ]);
      setData(queue);
      setAllowedRoles((roles?.allowed_roles || []).map(r => r.toLowerCase()));
    } catch (e) {
      toast.show(e.message, 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Fetch (or re-fetch) whenever the page changes. Fires on mount too since
  // rvPage is set from the start — no separate initial-load effect needed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load({ skip: rvPage.skip, limit: rvPage.limit }); }, [rvPage]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const measure = () => setViewportWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  const myRoles = useMemo(() => (currentUser?.roles || []).map(r =>
    (typeof r === 'string' ? r : (r.role_name || r.name || '')).toLowerCase()), [currentUser]);
  const canAct = useMemo(
    () => allowedRoles.length > 0 && myRoles.some(r => allowedRoles.includes(r)),
    [allowedRoles, myRoles],
  );

  // Memoised so the identity is stable — an inline `data?.rows || []` would be
  // a new array every render and re-run every downstream useMemo.
  const rows = useMemo(() => data?.rows || [], [data]);
  const departments = useMemo(
    () => [...new Set(rows.map(r => r.department_name).filter(Boolean))].sort(), [rows]);

  const filtered = rows.filter(r => {
    if (tab !== 'All' && r.review_status !== tab) return false;
    if (typeFilter && r.payment_type !== typeFilter) return false;
    if (deptFilter && r.department_name !== deptFilter) return false;
    if (advanceOnly === 'advance' && !r.is_advance) return false;
    if (advanceOnly === 'normal' && r.is_advance) return false;
    if (dateFrom && (!r.voucher_date || r.voucher_date < dateFrom)) return false;
    if (dateTo && (!r.voucher_date || r.voucher_date > dateTo)) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${r.voucher_no || ''} ${r.manual_voucher_no || ''} ${r.requisition_no || ''} ${r.vendor_name || ''} ${r.project_name || ''} ${r.review_status}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const pageNet = filtered.reduce((s, r) => s + Number(r.net_payable || 0), 0);
  const pagePaid = filtered.reduce((s, r) => s + Number(r.amount_paid || 0), 0);


  /** Fetch a voucher's full detail once and cache it. Shared by the expanded
   *  row, the history modal and Print, so opening any of them warms the others. */
  const loadDetail = async (row) => {
    const id = row.payment_voucher_id;
    if (detail[id]?.voucher) return detail[id];
    setDetail(p => ({ ...p, [id]: { loading: true } }));
    try {
      const voucher = await api.getPaymentVoucher(id);
      let tracking = null;
      if (row.workflow_instance_id) {
        try {
          // Keep the whole tracking object: the history timeline needs
          // current_step too, not just the completed steps.
          tracking = await api.getWorkflowTracking(row.workflow_instance_id);
        } catch { /* supplementary — the panel still renders without it */ }
      }
      const loaded = { voucher, tracking, history: tracking?.approval_history || [] };
      setDetail(p => ({ ...p, [id]: loaded }));
      return loaded;
    } catch (e) {
      const failed = { error: `Could not load this voucher: ${e.message}` };
      setDetail(p => ({ ...p, [id]: failed }));
      return failed;
    }
  };

  const toggleExpand = async (row) => {
    const id = row.payment_voucher_id;
    const opening = !expanded[id];
    setExpanded(p => ({ ...p, [id]: opening }));
    if (opening) await loadDetail(row);
  };

  /** Print one voucher, not the page. Fetches first if the row was never
   *  expanded, then waits a paint so the sheet is in the DOM before the
   *  browser dialog opens — printing too early yields a blank page. */
  const printVoucher = async (row) => {
    const d = await loadDetail(row);
    if (!d?.voucher) { toast.show(d?.error || 'Could not load this voucher for printing.', 'error'); return; }
    setPrinting(d);
    // Resolve only once the print sheet has painted and the dialog has been
    // asked for — otherwise the spinner cleared while the row was still
    // waiting for the two frames, which is most of the visible delay.
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        window.print();
        setPrinting(null);
        resolve();
      }));
    });
  };

  const availability = (row, a) => {
    if (a.key === 'view' || a.key === 'history' || a.key === 'print' || a.key === 'export') return true;
    if (!canAct) return false;
    if (a.key === 'release') return row.can_release;
    if (a.key === 'hold') return row.can_hold;
    // Editable while the voucher is still travelling the chain. Once it is
    // Approved for Payment or released the figures are settled, and a hold
    // must be lifted before anything is corrected.
    if (a.key === 'edit') return row.status === 'posted' && !row.on_hold && !row.released;
    if (a.workflow) return row.status === 'posted' && !row.on_hold && !!row.workflow_instance_id;
    return false;
  };

  const exportCsv = () => {
    const cols = ['voucher_no', 'manual_voucher_no', 'requisition_no', 'voucher_date', 'vendor_name',
      'payment_type', 'payment_method', 'lines_count', 'gross_amount', 'amount_payable', 'net_payable',
      'amount_processed', 'amount_paid', 'outstanding', 'department_name', 'project_name', 'priority', 'review_status'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cols.join(','), ...filtered.map(r => cols.map(c => esc(r[c])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `voucher-review-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // The edit modal needs the full voucher (line ids, current values), which
  // the queue row does not carry — fetch it the same way the expanded panel
  // does, reusing the cache when the row is already open.
  const openEdit = async (row) => {
    const cached = detail[row.payment_voucher_id]?.voucher;
    if (cached) return setEditing(cached);
    try {
      setEditing(await api.getPaymentVoucher(row.payment_voucher_id));
    } catch (e) {
      toast.show(e.message, 'error');
    }
    return undefined;
  };

  // Edit and History both fetch the full voucher before their modal can open,
  // and that round-trip was silent — the button looked dead for a beat. This
  // marks the exact button so the spinner appears where the click landed.
  const [busyBtn, setBusyBtn] = useState(null);   // `${voucherId}:${action}`

  const openAction = async (action, row) => {
    if (action === 'export') return exportCsv();
    if (action === 'view') return toggleExpand(row);

    // These three fetch the full voucher before anything can appear. Print
    // also has to wait two animation frames for the print sheet to lay out
    // before the browser dialog opens, so it is the slowest of the three.
    if (action === 'edit' || action === 'history' || action === 'print') {
      setBusyBtn(`${row.payment_voucher_id}:${action}`);
      try {
        if (action === 'edit') {
          await openEdit(row);
        } else if (action === 'print') {
          await printVoucher(row);
        } else {
          await loadDetail(row);
          setHistoryFor(row);
        }
      } finally {
        setBusyBtn(null);
      }
      return;
    }

    setPending({ action, row });
    setComment('');
    setActionError(null);
  };

  const runAction = async () => {
    const { action, row } = pending;
    const meta = ACTIONS.find(a => a.key === action);
    if ((meta.needsComment || action === 'hold') && !comment.trim()) {
      setActionError({ header: `A reason is required to ${action} this voucher.`, bullets: [] });
      return;
    }
    // A ref, not the `acting` state: setActing is async, so two clicks in the
    // same tick both get past the check before React re-renders and disables
    // the button — and the second one hits a voucher the first has already
    // released, producing "already been released" on what looked like a
    // single click. A ref updates synchronously, so the second call returns.
    if (actingRef.current) return;
    actingRef.current = true;

    setActing(true);
    setActionError(null);
    try {
      if (action === 'release') {
        await api.releasePaymentVoucher(row.payment_voucher_id, { comment: comment || null });
      } else if (action === 'hold') {
        await api.holdPaymentVoucher(row.payment_voucher_id, { on_hold: !row.on_hold, reason: comment });
      } else {
        await api.actOnWorkflow(row.workflow_instance_id, { action, comment: comment || null });
      }
      setPending(null);
      // Lifting a hold is its own outcome, not a hold — otherwise the modal
      // showed pause bars and "VOUCHER ON HOLD!" right after resuming it.
      const lifting = action === 'hold' && row.on_hold;
      const OUT = {
        release: ['release', 'Voucher Released', `${row.voucher_no} has been released to the Expense Register.`],
        hold: ['hold', 'Voucher Held', `${row.voucher_no} is on hold and cannot be acted on until the hold is lifted.`],
        resume: ['resume', 'Hold Lifted', `${row.voucher_no} is active again and can be acted on.`],
        approve: ['approve', 'Voucher Approved', `${row.voucher_no} has been approved for payment.`],
        return: ['return', 'Voucher Returned', `${row.voucher_no} has been sent back for correction.`],
        reject: ['reject', 'Voucher Rejected', `${row.voucher_no} has been rejected.`],
      };
      const [type, title, message] = OUT[lifting ? 'resume' : action] || [action, 'Done', `${row.voucher_no} has been updated.`];
      setOutcome({ type, title, message });

      // Silent: the table refreshes behind the confirmation instead of
      // replacing the whole screen with the full-page loader.
      await load({ silent: true });
    } catch (e) {
      setActionError(parsePvErrorMessage(e.message));
    } finally {
      actingRef.current = false;
      setActing(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><RNLLoader title="Loading review queue…" /></div>;

  const stats = data?.stats || {};
  const counts = data?.tab_counts || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Voucher Review &amp; Release</h2>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            Review, verify and release approved payment vouchers into the Expense Register.
            {!canAct && allowedRoles.length > 0 && (
              <span style={{ color: '#b45309', fontWeight: 700 }}>
                {' '}View only — acting here is limited to {allowedRoles.join(', ')}.
              </span>
            )}
          </div>
        </div>
        <Btn size="sm" variant="outline" onClick={() => setHeaderOpen(o => !o)}>
          {headerOpen ? '⌃ Collapse Header' : '⌄ Expand Header'}
        </Btn>
      </div>

      {headerOpen && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
          {TILES.map(([key, label, color, money]) => (
            <div key={key} style={{ border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 12px', background: '#fff' }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
              <div style={{ fontSize: money ? 14 : 20, fontWeight: 800, color, marginTop: 3 }}>
                {money ? fmt(stats[key]) : (stats[key] ?? 0)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const active = tab === t;
          return (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '7px 13px', borderRadius: 9, cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
              border: `1px solid ${active ? NAVY : '#cbd5e1'}`,
              background: active ? NAVY : '#fff', color: active ? '#fff' : '#475569',
              display: 'inline-flex', alignItems: 'center', gap: 7,
            }}>
              {t}
              <span style={{
                background: active ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                color: active ? '#fff' : '#475569', borderRadius: 999, padding: '1px 7px', fontSize: 10, fontWeight: 800,
              }}>{counts[t] ?? 0}</span>
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <Input icon="🔍" placeholder="Search voucher, manual no, requisition, vendor, project…" value={search} onChange={setSearch} />
        </div>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          style={{ padding: '9px 11px', borderRadius: 9, border: '1px solid #cbd5e1', fontSize: 12 }} />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          style={{ padding: '9px 11px', borderRadius: 9, border: '1px solid #cbd5e1', fontSize: 12 }} />
        <div style={{ width: 180 }}>
          <Select value={typeFilter} onChange={setTypeFilter} options={[{ value: '', label: 'All Types' }, ...PAYMENT_TYPE_OPTIONS]} />
        </div>
        <div style={{ width: 200 }}>
          <Select value={deptFilter} onChange={setDeptFilter} options={[{ value: '', label: 'All Departments' }, ...departments.map(d => ({ value: d, label: d }))]} />
        </div>
        <div style={{ width: 150 }}>
          <Select value={advanceOnly} onChange={setAdvanceOnly} options={[
            { value: '', label: 'All' }, { value: 'advance', label: 'Advance Only' }, { value: 'normal', label: 'Non-Advance' },
          ]} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary)', flexWrap: 'wrap', gap: 8 }}>
        <span>Showing <strong>{filtered.length}</strong> of {rows.length} vouchers</span>

        <span>Net Payable: <strong style={{ color: '#15803d' }}>{fmt(pageNet)}</strong> · Paid: <strong style={{ color: '#1d4ed8' }}>{fmt(pagePaid)}</strong></span>
      </div>

      <Card style={{ border: '1px solid black', borderRadius: 8, boxShadow: 'none', padding: 0, overflow: 'hidden' }}>
        <div ref={scrollRef} style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 1900,
            // Cells draw right + bottom only, so the table supplies the
            // outermost top and left edges to close the grid.
            borderTop: GRID, borderLeft: GRID,
          }}>
            <thead>
              <tr style={{ background: '#243b64', color: '#fff' }}>
                {['', 'Actions', 'Voucher Ref', 'Requisition Ref', 'Voucher Date', 'Vendor', 'Payment Type',
                  'Payment Method', 'Lines', 'Gross', 'Amount Payable', 'Net Payable', 'Processed', 'Paid',
                  'Outstanding', 'Department', 'Project', 'Priority', 'Status'].map((h, i) => (
                    <th key={i} style={{ ...TH, ...frozen(i, '#243b64', true) }}>{h}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={19} style={{ ...TD, textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
                  No vouchers match these filters.
                </td></tr>
              )}
              {filtered.map(r => {
                const sc = STATUS_COLORS[r.review_status] || { bg: '#f1f5f9', color: '#475569' };
                const open = !!expanded[r.payment_voucher_id];
                // Opaque on purpose: frozen cells are painted over the columns
                // scrolling beneath them, and 'transparent' would let those
                // show through the sticky region.
                const rowBg = open ? '#f8fafc' : '#ffffff';
                return (
                  <React.Fragment key={r.payment_voucher_id}>
                    <tr style={{ background: rowBg }}>
                      <td style={{ ...TD, ...frozen(0, rowBg), cursor: 'pointer' }} onClick={() => toggleExpand(r)}>
                        <span style={{ color: NAVY, fontWeight: 800 }}>{open ? '⌄' : '›'}</span>
                      </td>
                      <td style={{ ...TD, ...frozen(1, rowBg), padding: '7px 6px' }}>
                        <div style={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                          {ACTIONS.map(a => {
                            const on = availability(r, a);
                            const lifting = a.key === 'hold' && r.on_hold;
                            const label = lifting ? 'Lift Hold — resume this voucher' : a.title;
                            const iconKey = lifting ? 'resume' : a.key;
                            return (
                              <button key={a.key} title={label} disabled={!on || !!busyBtn}
                                onClick={() => on && openAction(a.key, r)}
                                style={iconBtn(ACTION_COLOR[iconKey] || NAVY, !on)}>
                                {busyBtn === `${r.payment_voucher_id}:${a.key}`
                                  ? <Spinner size={12} />
                                  : ICONS[iconKey]}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td style={{ ...TD, ...frozen(2, rowBg), cursor: 'pointer' }} onClick={() => toggleExpand(r)}>
                        <div style={{ fontFamily: 'var(--mono)', color: NAVY, fontWeight: 700 }}>{r.voucher_no || '—'}</div>
                        {r.manual_voucher_no && <div style={{ fontSize: 9.5, color: '#94a3b8' }}>{r.manual_voucher_no}</div>}
                      </td>
                      <td style={{ ...TD, ...frozen(3, rowBg) }}>{r.requisition_no || '—'}</td>
                      <td style={{ ...TD, ...frozen(4, rowBg) }}>{r.voucher_date || '—'}</td>
                      <td style={{ ...TD, ...frozen(5, rowBg), overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={r.vendor_name || undefined}>{r.vendor_name || '—'}</td>
                      <td style={TD}>{paymentTypeLabel(r.payment_type)}</td>
                      <td style={TD}>{r.payment_method || '—'}</td>
                      <td style={TD}>{r.lines_count}</td>
                      <td style={TD}>{fmt(r.gross_amount)}</td>
                      <td style={TD}>{fmt(r.amount_payable)}</td>
                      <td style={{ ...TD, fontWeight: 700 }}>{fmt(r.net_payable)}</td>
                      <td style={TD}>{fmt(r.amount_processed)}</td>
                      <td style={{ ...TD, color: '#2563eb' }}>{fmt(r.amount_paid)}</td>
                      <td style={{ ...TD, color: '#c2410c', fontWeight: 700 }}>{fmt(r.outstanding)}</td>
                      <td style={TD}>{r.department_name || '—'}</td>
                      <td style={TD}>{r.project_name || '—'}</td>
                      <td style={TD}>{r.priority || '—'}</td>
                      <td style={TD}>
                        <span title={r.hold_reason || undefined} style={{
                          background: sc.bg, color: sc.color, border: sc.border ? `1px solid ${sc.border}` : 'none',
                          padding: '3px 9px', borderRadius: 999, fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase',
                        }}>{r.review_status}</span>
                      </td>
                    </tr>
                    {open && (
                      <tr><td colSpan={19} style={{ padding: 0, borderBottom: '2px solid #cbd5e1' }}>
                        {/* sticky-left + a width tied to the visible container
                            keeps the detail stationary while the table scrolls
                            behind it, so it can never end up under the frozen
                            columns — and lets section 4 scroll on its own. */}
                        <div style={{
                          position: 'sticky', left: 0,
                          width: viewportWidth || '100%',
                          maxWidth: viewportWidth || undefined,
                          overflow: 'hidden',
                        }}>
                          <VoucherReviewDetail {...(detail[r.payment_voucher_id] || {})} />
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Pagination total={data?.total_count ?? 0} skip={rvPage.skip} limit={rvPage.limit} onChange={setRvPage} />

      {pending && (
        <Modal title={`${ACTIONS.find(a => a.key === pending.action).title} — ${pending.row.voucher_no}`}
          onClose={() => setPending(null)} width={520}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
              {pending.action === 'release'
                ? 'This puts the voucher into the Expense Register and marks the money as gone out. It cannot be undone from this screen.'
                : pending.action === 'hold'
                  ? (pending.row.on_hold ? 'Lift the hold so this voucher can be acted on again.' : 'Pause this voucher. It keeps its place in the approval chain and every approval already given.')
                  : `Net payable: ${fmt(pending.row.net_payable)}`}
            </div>
            <Textarea
              label={pending.action === 'release' ? 'Comment (optional)' : 'Reason (required)'}
              value={comment} onChange={setComment} rows={3}
              placeholder={pending.action === 'hold' ? 'Why is this voucher being held?' : 'Add a note…'}
            />
            {actionError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '9px 12px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c' }}>{actionError.header}</div>
                {actionError.bullets?.map((b, i) => <div key={i} style={{ fontSize: 11.5, color: '#b91c1c' }}>• {b}</div>)}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="outline" disabled={acting} onClick={() => setPending(null)}>Cancel</Btn>
              <Btn loading={acting} onClick={runAction}>Confirm</Btn>
            </div>
          </div>
        </Modal>
      )}

      {historyFor && (
        <Modal title={`Approval History — ${historyFor.voucher_no}`} onClose={() => setHistoryFor(null)} width={1180}>
          <VoucherApprovalHistory {...(detail[historyFor.payment_voucher_id] || { loading: true })} />
        </Modal>
      )}

      {/* `outcome={outcome}`, NOT {...outcome}: the component reads an
          `outcome` prop and bails on `if (!outcome) return null`, so spreading
          made it render nothing at all. */}
      {outcome && <VoucherOutcomeModal outcome={outcome} onClose={() => setOutcome(null)} />}

      {/* Hidden on screen; @media print hides the app and reveals only this. */}
      {editing && (
        <VoucherEditModal
          voucher={editing}
          toast={toast}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setDetail({});      // corrected figures must not be served from cache
            load();
          }}
        />
      )}

      {printing && <VoucherPrintView voucher={printing.voucher} history={printing.history} />}
    </div>
  );
}
